import { useMemo } from "react"
import { ConnectionHandler, graphql, GraphQLSubscriptionConfig } from "relay-runtime"
import { useIsFeatureEnabled } from "@/hooks/useIsFeatureEnabled"
import { useCallTranscriptSubscriptionChSubscription } from "@/modules/patients/detail/progress-note/hooks/__generated__/useCallTranscriptSubscriptionChSubscription.graphql"
import { useCallTranscriptSubscriptionDeSubscription } from "@/modules/patients/detail/progress-note/hooks/__generated__/useCallTranscriptSubscriptionDeSubscription.graphql"
import { useConditionalSubscription } from "@/modules/patients/hooks/useConditionalSubscription"
import { CommunicationChannel, Country } from "@/types/__generated__/union-types"

const chSubscription = graphql`
  subscription useCallTranscriptSubscriptionChSubscription($appointmentId: ID!) {
    ch_onCallTranscriptUpdated(appointmentId: $appointmentId) {
      id
      transcript {
        id
        text
      }
      callTranscriptEndedAt
    }
  }
`

const deSubscription = graphql`
  subscription useCallTranscriptSubscriptionDeSubscription($appointmentId: ID!) {
    de_onCallTranscriptUpdated(appointmentId: $appointmentId) {
      id
      transcript {
        id
        text
      }
      callTranscriptEndedAt
    }
  }
`

export const useCallTranscriptSubscription = (
  appointmentId: string,
  progressNoteId: string,
  country: Country,
  appointmentStatus: string,
  channel: CommunicationChannel,
) => {
  const isSttEnabled = useIsFeatureEnabled("SPEECH_TO_TEXT")
  const isCallChannel = channel === "PHONE" || channel === "VIDEO"
  const isActive = appointmentStatus === "IN_PROGRESS" && isSttEnabled && isCallChannel
  const connectionKey =
    country === "CH" ? "ProgressNoteConnection_ch_callTranscripts" : "ProgressNoteConnection_de_callTranscripts"

  const config: GraphQLSubscriptionConfig<
    useCallTranscriptSubscriptionChSubscription | useCallTranscriptSubscriptionDeSubscription
  > = useMemo(
    () => ({
      subscription: country === "CH" ? chSubscription : deSubscription,
      variables: { appointmentId },
      onError: (error: Error) => console.error(`An error occurred while subscribe to CallTranscript:`, error),
      updater(store, data) {
        try {
          if (!data) return
          const transcript =
            "ch_onCallTranscriptUpdated" in data ? data.ch_onCallTranscriptUpdated : data.de_onCallTranscriptUpdated
          if (!transcript) return

          // Relay auto-merges fields for existing nodes by id.
          // We only need to manually add the node to the connection if it's new.
          const progressNoteProxy = store.get(progressNoteId)
          if (!progressNoteProxy) return

          const connection = ConnectionHandler.getConnection(progressNoteProxy, connectionKey)
          if (!connection) return

          const edges = connection.getLinkedRecords("edges") ?? []
          const alreadyInConnection = edges.some((edge) => {
            const node = edge.getLinkedRecord("node")
            return node?.getDataID() === transcript.id
          })

          if (!alreadyInConnection) {
            const newNode = store.get(transcript.id)
            if (!newNode) return

            const newEdge = ConnectionHandler.createEdge(store, connection, newNode, "CallTranscriptEdge")
            ConnectionHandler.insertEdgeAfter(connection, newEdge)
          }
        } catch (error) {
          console.error("CallTranscript subscription updater error:", error)
        }
      },
    }),
    [appointmentId, progressNoteId, country, connectionKey],
  )

  useConditionalSubscription<useCallTranscriptSubscriptionChSubscription | useCallTranscriptSubscriptionDeSubscription>(
    config,
    { skip: !isActive },
  )
}
